import React, { useState, useCallback } from 'react';
import { View, StyleSheet, FlatList, TouchableOpacity, RefreshControl } from 'react-native';
import { Text, Card, ActivityIndicator, Divider, FAB } from 'react-native-paper';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import api from '../../services/api';

const ESTADOS_CONFIG = {
  vigente:    { label: 'Vigente',    color: '#28A745', icon: 'checkmark-circle',  textColor: '#fff' },
  vencida:    { label: 'Vencida',    color: '#FFC107', icon: 'time',              textColor: '#000' },
  convertida: { label: 'Convertida', color: '#17A2B8', icon: 'swap-horizontal',  textColor: '#fff' },
  cancelada:  { label: 'Cancelada',  color: '#DC3545', icon: 'close-circle',      textColor: '#fff' },
};

const CotizacionesScreen = () => {
  const navigation = useNavigation();
  const insets = useSafeAreaInsets();
  const [cotizaciones, setCotizaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filtro, setFiltro] = useState('todas');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getCotizaciones();
      setCotizaciones(data);
    } catch (error) {
      console.error('Error al cargar cotizaciones:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      loadData();
    }, [loadData])
  );

  const formatFecha = (fecha) => {
    if (!fecha) return '';
    const date = new Date(fecha);
    return date.toLocaleDateString('es-HN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const isVencida = (cotizacion) => {
    if (cotizacion.estado !== 'vigente') return false;
    if (!cotizacion.fecha_vencimiento) return false;
    return new Date(cotizacion.fecha_vencimiento) < new Date();
  };

  const filteredCotizaciones = filtro === 'todas'
    ? cotizaciones
    : cotizaciones.filter(c => {
        if (filtro === 'vencida') return c.estado === 'vigente' && isVencida(c);
        return c.estado === filtro;
      });

  const renderItem = ({ item }) => {
    const vencida = isVencida(item);
    const estadoKey = vencida ? 'vencida' : item.estado;
    const config = ESTADOS_CONFIG[estadoKey] || ESTADOS_CONFIG.vigente;

    return (
      <TouchableOpacity
        onPress={() => navigation.navigate('DetalleCotizacion', { cotizacion: item })}
      >
        <Card style={styles.card}>
          <Card.Content>
            <View style={styles.header}>
              <View style={styles.info}>
                <View style={styles.idRow}>
                  <Text style={styles.numero}>{item.numero}</Text>
                  <View style={[styles.estadoChip, { backgroundColor: config.color }]}>
                    <Ionicons name={config.icon} size={12} color={config.textColor} />
                    <Text style={[styles.estadoChipText, { color: config.textColor }]}>{config.label}</Text>
                  </View>
                </View>
                <Text style={styles.cliente}>
                  <Ionicons name="person" size={14} color="#666" /> {item.cliente_nombre || 'Sin cliente'}
                </Text>
                <Text style={styles.vendedor}>
                  <Ionicons name="business" size={14} color="#888" /> {item.vendedor_nombre || 'N/A'}
                </Text>
              </View>
              <View style={styles.right}>
                <Text style={styles.total}>L. {parseFloat(item.total).toFixed(2)}</Text>
                <Text style={styles.items}>{item.total_items || 0} items</Text>
              </View>
            </View>

            <Divider style={styles.divider} />

            <View style={styles.footer}>
              <Text style={styles.fecha}>
                <Ionicons name="calendar" size={12} color="#888" /> {formatFecha(item.fecha)}
              </Text>
              {item.fecha_vencimiento && (
                <Text style={[styles.vencimiento, vencida && { color: '#DC3545' }]}>
                  <Ionicons name="time" size={12} color={vencida ? '#DC3545' : '#888'} />
                  {' '}Vence: {formatFecha(item.fecha_vencimiento)}
                </Text>
              )}
              {item.venta_id && (
                <Text style={styles.ventaLink}>
                  <Ionicons name="cart" size={12} color="#17A2B8" /> Venta #{item.venta_id}
                </Text>
              )}
            </View>
          </Card.Content>
        </Card>
      </TouchableOpacity>
    );
  };

  if (loading && !refreshing) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#007bff" />
        <Text style={styles.loadingText}>Cargando cotizaciones...</Text>
      </View>
    );
  }

  const conteo = {
    todas: cotizaciones.length,
    vigente: cotizaciones.filter(c => c.estado === 'vigente' && !isVencida(c)).length,
    vencida: cotizaciones.filter(c => isVencida(c)).length,
    convertida: cotizaciones.filter(c => c.estado === 'convertida').length,
  };

  return (
    <View style={styles.container}>
      {/* Filtros */}
      <View style={styles.filtrosContainer}>
        {['todas', 'vigente', 'vencida', 'convertida'].map(f => (
          <TouchableOpacity
            key={f}
            style={[styles.filtroChip, filtro === f && styles.filtroActivo]}
            onPress={() => setFiltro(f)}
          >
            <Text style={[styles.filtroText, filtro === f && styles.filtroTextoActivo]}>
              {f === 'todas' ? 'Todas' : ESTADOS_CONFIG[f]?.label || f} ({conteo[f] || 0})
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      <FlatList
        data={filteredCotizaciones}
        keyExtractor={(item) => item.id.toString()}
        renderItem={renderItem}
        contentContainerStyle={styles.listContainer}
        ItemSeparatorComponent={() => <View style={{ height: 8 }} />}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); loadData(); }} />
        }
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <Ionicons name="document-text-outline" size={64} color="#ccc" />
            <Text style={styles.emptyText}>No hay cotizaciones</Text>
          </View>
        }
      />

      <FAB
        icon="plus"
        style={[styles.fab, { bottom: 16 + insets.bottom }]}
        onPress={() => navigation.navigate('NuevaCotizacion')}
        label="Nueva"
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5' },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  loadingText: { marginTop: 10, color: '#666' },
  filtrosContainer: { flexDirection: 'row', padding: 8, flexWrap: 'wrap' },
  filtroChip: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 16, backgroundColor: '#E9ECEF', marginRight: 6, marginBottom: 4 },
  filtroActivo: { backgroundColor: '#007BFF' },
  filtroText: { fontSize: 12, color: '#666' },
  filtroTextoActivo: { color: '#fff', fontWeight: 'bold' },
  listContainer: { padding: 8, paddingBottom: 80 },
  card: { elevation: 2 },
  header: { flexDirection: 'row', justifyContent: 'space-between' },
  info: { flex: 1, marginRight: 10 },
  idRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 4, flexWrap: 'wrap' },
  numero: { fontSize: 15, fontWeight: 'bold', color: '#333', marginRight: 8 },
  estadoChip: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 8, paddingVertical: 3, borderRadius: 12, gap: 4 },
  estadoChipText: { fontSize: 10, fontWeight: 'bold' },
  cliente: { fontSize: 13, color: '#666', marginBottom: 2 },
  vendedor: { fontSize: 12, color: '#888' },
  right: { alignItems: 'flex-end' },
  total: { fontSize: 16, fontWeight: 'bold', color: '#28a745' },
  items: { fontSize: 12, color: '#888', marginTop: 2 },
  divider: { marginVertical: 8 },
  footer: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' },
  fecha: { fontSize: 12, color: '#888' },
  vencimiento: { fontSize: 12, color: '#888' },
  ventaLink: { fontSize: 12, color: '#17A2B8' },
  emptyContainer: { justifyContent: 'center', alignItems: 'center', paddingVertical: 50 },
  emptyText: { marginTop: 10, color: '#888', fontSize: 16 },
  fab: { position: 'absolute', right: 16, bottom: 16, backgroundColor: '#007BFF' },
});

export default CotizacionesScreen;
